import { useState, useMemo } from "react";
import {
  rankPlayersAndFormatWinners,
  calculatePlayersWithScores,
} from "pronosticsv2/utils/scoringUtils";
import type { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import type { Player } from "quiz/types/player.model";

interface PlayerEntry {
  name: string;
  ftHome: string;
  ftAway: string;
  htHome: string;
  htAway: string;
  minute: string;
}

const emptyPlayer = (): PlayerEntry => ({
  name: "",
  ftHome: "",
  ftAway: "",
  htHome: "",
  htAway: "",
  minute: "",
});

export function Pronosticv2TestPageView() {
  const [ftHome, setFtHome] = useState("");
  const [ftAway, setFtAway] = useState("");
  const [htHome, setHtHome] = useState("");
  const [htAway, setHtAway] = useState("");
  const [lastMinuteGoal, setLastMinuteGoal] = useState("");
  const [players, setPlayers] = useState<PlayerEntry[]>([emptyPlayer()]);

  const updatePlayer = (index: number, field: keyof PlayerEntry, value: string) => {
    setPlayers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addPlayer = () => setPlayers((prev) => [...prev, emptyPlayer()]);

  const removePlayer = (index: number) => {
    setPlayers((prev) => prev.filter((_, i) => i !== index));
  };

  const ranking = useMemo(() => {
    const ftH = Number(ftHome);
    const ftA = Number(ftAway);
    const htH = Number(htHome);
    const htA = Number(htAway);
    const min = Number(lastMinuteGoal);

    if ([ftHome, ftAway, htHome, htAway, lastMinuteGoal].some((v) => v === "")) {
      return null;
    }

    const validPlayers = players.filter(
      (p) =>
        p.name.trim() !== "" &&
        p.ftHome !== "" &&
        p.ftAway !== "" &&
        p.htHome !== "" &&
        p.htAway !== "" &&
        p.minute !== "",
    );

    if (validPlayers.length === 0) return null;

    const mockPronosticV2: PronosticV2 = {
      id: "test",
      partitionId: "test",
      name: "Test",
      owner: { L1: "", L2: "", L3: "", L4: "" },
      parentId: "",
      status: "ongoing",
      type: "pronosticV2",
      createdAt: "",
      commonSettings: {
        general: {
          moduleSettings: {
            place: { placeId: "", placeTitle: "", subPlaces: [] },
            pronostics: [
              {
                index: 1,
                type: "input",
                element1Title: "Home",
                element2Visual: "",
                element3Title: "Away",
                element4FieldSize: 1,
                element5FieldSize: 1,
                element6Title: "",
                element7Visual: "",
              },
              {
                index: 2,
                type: "input",
                element1Title: "HT Home",
                element2Visual: "",
                element3Title: "HT Away",
                element4FieldSize: 1,
                element5FieldSize: 1,
                element6Title: "",
                element7Visual: "",
              },
              {
                index: 3,
                type: "input",
                element1Title: "Minute",
                element2Visual: "",
                element3Title: "",
                element4FieldSize: 1,
                element5FieldSize: 0,
                element6Title: "",
                element7Visual: "",
              },
            ],
            winCondition: {
              type: "OrderedAccuracy",
              content: [
                { winConditionIndex: 1, pronosticIndex: 1, type: "exact" },
                { winConditionIndex: 2, pronosticIndex: 2, type: "exact" },
                { winConditionIndex: 3, pronosticIndex: 3, type: "closest" },
              ],
            },
          },
        },
        webApp: {
          moduleSettings: {
            messageTitleColor: "",
            messageTitleSize: "",
            pronoTitleColor: "",
            pronoTitleSize: "",
            buttonBgColor: "",
            buttonTextColor: "",
            mandatoryTextColor: "",
            mandatoryTextSize: "",
          },
          medias: { backGroundImage: "" },
          texts: {
            welcomeMessage: "",
            footerMessage: "",
            thankYouMessage: "",
            noResultMessage: "",
            notStartedMessage: "",
            tooLateMessage: "",
            mandatoryMessage: "",
            submitButtonLabel: "",
            backHomeButtonLabel: "",
            resultsButtonLabel: "",
            refreshButtonLabel: "",
          },
        },
        publicSharing: {
          moduleSettings: {
            messagesColor: "",
            messagesSize: "",
            screenTopMargin: "",
            screenLeftMargin: "",
          },
          medias: { backgroundImage: "", sponsorImage: "" },
          texts: { introductionMessage: "" },
        },
      },
      specificSettings: {
        beginAt: "",
        endAt: "",
        pronosticResults: [
          { pronosticIndex: 1, field1: ftH, field2: ftA },
          { pronosticIndex: 2, field1: htH, field2: htA },
          { pronosticIndex: 3, field1: min, field2: null },
        ],
        resultsPublication: false,
        winnersInfo: [],
      },
    };

    const mockPlayers: Player[] = validPlayers.map((p, i) => ({
      id: `player-${i}`,
      partitionId: "test",
      firstName: p.name.trim(),
      lastName: "",
      email: "",
      phoneNumber: "",
      connected: 1,
      pronosticInfo: {
        prediction: [
          { index: 1, values: [Number(p.ftHome), Number(p.ftAway)] },
          { index: 2, values: [Number(p.htHome), Number(p.htAway)] },
          { index: 3, values: [Number(p.minute)] },
        ],
      },
    }));

    try {
      const winners = rankPlayersAndFormatWinners(mockPlayers, mockPronosticV2, "0");
      const playersWithScores = calculatePlayersWithScores(mockPlayers, mockPronosticV2);

      // Build a map from player id to metrics
      const metricsMap: Record<string, Record<string, any>> = {};
      for (const pws of playersWithScores) {
        metricsMap[pws.player.id] = pws.metrics || {};
      }

      return { winners, metricsMap };
    } catch (e) {
      console.error("Ranking error:", e);
      return null;
    }
  }, [ftHome, ftAway, htHome, htAway, lastMinuteGoal, players]);

  const inputClass =
    "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
  const smallInputClass =
    "w-16 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center";
  const labelClass = "block text-sm font-medium text-gray-700";

  return (
    <div className="p-4 w-full">
      <h1 className="text-2xl font-bold mb-6">PronosticV2 Ranking Test Page</h1>

      {/* Actual Results */}
      <section className="mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h2 className="text-lg font-semibold mb-4">Actual Results</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Full-Time Score</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min="0"
                value={ftHome}
                onChange={(e) => setFtHome(e.target.value)}
                className={smallInputClass}
                placeholder="H"
              />
              <span className="font-bold">-</span>
              <input
                type="number"
                min="0"
                value={ftAway}
                onChange={(e) => setFtAway(e.target.value)}
                className={smallInputClass}
                placeholder="A"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Half-Time Score</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min="0"
                value={htHome}
                onChange={(e) => setHtHome(e.target.value)}
                className={smallInputClass}
                placeholder="H"
              />
              <span className="font-bold">-</span>
              <input
                type="number"
                min="0"
                value={htAway}
                onChange={(e) => setHtAway(e.target.value)}
                className={smallInputClass}
                placeholder="A"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Last Minute Goal</label>
            <input
              type="number"
              min="0"
              value={lastMinuteGoal}
              onChange={(e) => setLastMinuteGoal(e.target.value)}
              className={inputClass}
              placeholder="Minute"
            />
          </div>
        </div>
      </section>

      {/* Player Predictions */}
      <section className="mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h2 className="text-lg font-semibold mb-4">Player Predictions</h2>
        <div className="space-y-3">
          {/* Header */}
          <div className="hidden md:grid md:grid-cols-[1fr_auto_auto_auto_auto] gap-2 text-xs font-medium text-gray-500 px-1">
            <span>Name</span>
            <span className="w-36 text-center">FT Prediction</span>
            <span className="w-36 text-center">HT Prediction</span>
            <span className="w-16 text-center">Minute</span>
            <span className="w-8" />
          </div>
          {players.map((player, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center bg-white p-2 rounded border border-gray-100"
            >
              <input
                type="text"
                value={player.name}
                onChange={(e) => updatePlayer(index, "name", e.target.value)}
                className={inputClass}
                placeholder="Player name"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  value={player.ftHome}
                  onChange={(e) => updatePlayer(index, "ftHome", e.target.value)}
                  className={smallInputClass}
                  placeholder="H"
                />
                <span>-</span>
                <input
                  type="number"
                  min="0"
                  value={player.ftAway}
                  onChange={(e) => updatePlayer(index, "ftAway", e.target.value)}
                  className={smallInputClass}
                  placeholder="A"
                />
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  value={player.htHome}
                  onChange={(e) => updatePlayer(index, "htHome", e.target.value)}
                  className={smallInputClass}
                  placeholder="H"
                />
                <span>-</span>
                <input
                  type="number"
                  min="0"
                  value={player.htAway}
                  onChange={(e) => updatePlayer(index, "htAway", e.target.value)}
                  className={smallInputClass}
                  placeholder="A"
                />
              </div>
              <input
                type="number"
                min="0"
                value={player.minute}
                onChange={(e) => updatePlayer(index, "minute", e.target.value)}
                className={smallInputClass}
                placeholder="Min"
              />
              <button
                type="button"
                onClick={() => removePlayer(index)}
                className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded"
                disabled={players.length <= 1}
              >
                X
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addPlayer}
          className="mt-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Add Player
        </button>
      </section>

      {/* Ranking Results */}
      <section className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h2 className="text-lg font-semibold mb-4">Ranking</h2>
        {ranking && ranking.winners.winner && ranking.winners.winner.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th rowSpan={2} className="px-3 py-2 text-left font-medium text-gray-500 uppercase border-r">
                    Rank
                  </th>
                  <th rowSpan={2} className="px-3 py-2 text-left font-medium text-gray-500 uppercase border-r">
                    Player
                  </th>
                  <th rowSpan={2} className="px-3 py-2 text-left font-medium text-gray-500 uppercase border-r">
                    Prediction
                  </th>
                  <th colSpan={8} className="px-3 py-1 text-center font-medium text-blue-600 uppercase border-r bg-blue-50">
                    FT Metrics
                  </th>
                  <th colSpan={8} className="px-3 py-1 text-center font-medium text-green-600 uppercase border-r bg-green-50">
                    HT Metrics
                  </th>
                  <th className="px-3 py-1 text-center font-medium text-purple-600 uppercase bg-purple-50">
                    Min Metric
                  </th>
                </tr>
                <tr>
                  {/* FT metric headers */}
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Exact Score (1=exact match)">ExSc</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Exact Result (1=correct W/D/L)">ExRes</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Winner Exact Value (1=winner's goals match)">WEV</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Result Gap (distance between result directions)">RGap</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Sum of Difference+Goals gaps">SDG</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Abs difference of Difference-Goals gaps">ADG</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50" title="Difference Gap (goal difference gap)">DGap</th>
                  <th className="px-2 py-1 text-center font-medium text-blue-500 bg-blue-50 border-r" title="Goals Number (total predicted goals, 0 for draws)">GNum</th>
                  {/* HT metric headers */}
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Exact Score">ExSc</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Exact Result">ExRes</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Winner Exact Value">WEV</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Result Gap">RGap</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Sum Difference Goals">SDG</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Abs Difference Goals">ADG</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50" title="Difference Gap">DGap</th>
                  <th className="px-2 py-1 text-center font-medium text-green-500 bg-green-50 border-r" title="Goals Number">GNum</th>
                  {/* Minute metric header */}
                  <th className="px-2 py-1 text-center font-medium text-purple-500 bg-purple-50" title="Minute Gap (absolute distance from actual)">MinGap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ranking.winners.winner.map((w, i) => {
                  const m = ranking.metricsMap[w.id] || {};
                  const ft = m.pred1 || {};
                  const ht = m.pred2 || {};
                  const min = m.pred3 || {};
                  return (
                    <tr key={i} className={w.rank === 1 ? "bg-yellow-50" : ""}>
                      <td className="px-3 py-2 font-medium border-r">{w.rank}</td>
                      <td className="px-3 py-2 border-r">{w.name}</td>
                      <td className="px-3 py-2 font-mono border-r">{w.predictionString}</td>
                      {/* FT metrics */}
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.exactScore ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.exactResult ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.winnerExactValue ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.resultGap ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.sumDifferenceGoals ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.absDifferenceGoals ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30">{ft.differenceGap ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-blue-50/30 border-r">{ft.goalsNumber ?? "-"}</td>
                      {/* HT metrics */}
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.exactScore ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.exactResult ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.winnerExactValue ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.resultGap ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.sumDifferenceGoals ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.absDifferenceGoals ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30">{ht.differenceGap ?? "-"}</td>
                      <td className="px-2 py-1 text-center bg-green-50/30 border-r">{ht.goalsNumber ?? "-"}</td>
                      {/* Minute metric */}
                      <td className="px-2 py-1 text-center bg-purple-50/30">{min.minuteGap ?? "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Enter actual results and at least one player prediction to see rankings.
          </p>
        )}
      </section>

      {/* Sort Priority Legend */}
      <section className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h3 className="text-sm font-semibold mb-2 text-gray-600">Sort Priority (OrderedAccuracy)</h3>
        <div className="text-xs text-gray-500 space-y-1">
          <p><span className="font-medium text-blue-600">FT:</span> ExactScore (DESC) → ExactResult (DESC) → WinnerExactValue (DESC) → ResultGap (ASC) → SumDiffGoals (ASC) → AbsDiffGoals (ASC) → DiffGap (ASC) → GoalsNumber (DESC)</p>
          <p><span className="font-medium text-green-600">HT:</span> Same metric order as FT</p>
          <p><span className="font-medium text-purple-600">Minute:</span> MinuteGap (ASC) — lower is better</p>
        </div>
      </section>
    </div>
  );
}
